import React, { useMemo, useState, useCallback } from "react";
// Link removed — viewAllLink unused after dashboard redesign
import type { SalesTransaction } from "../../types";
import { formatUSD, formatDateShort } from "@/utils/formatters";
import LeadTimelineModal from "../LeadTimelineModal";
import OutcomeSummaryStrip from "./OutcomeSummaryStrip";
import FilterBar from "./FilterBar";
import CountryFilterFlyout, { type CountryOption } from "./CountryFilterFlyout";
import { BUCKETS, classifyOutcome } from "../../lib/outcomeBuckets";
import type { FilterableColumn, FilterMode, FilterChip } from "../../hooks/useTransactionFilters";
import { getColumnValue } from "../../hooks/useTransactionFilters";
import { toTitleCase } from "../../lib/titleCase";
import { isSelfSet } from "../../utils/selfSet";
import MiniSparkline from "./MiniSparkline";
import { timestampToOrgDateKey } from "../../lib/timezone";
import { useScrollReveal } from "../../hooks/useScrollReveal";

// =============================================================================
// TRANSACTION LOG
// =============================================================================
// PURPOSE: Embedded transaction list for dashboard (5-row snippet) and
// TransactionLogPage (full pagination + click-to-filter + Fathom playback).
//
// MODES:
// - snippet=true  → 5 rows, no toggles/filters/Fathom (OperationsDashboard)
// - snippet=false → Full table with tabs, sort, filters, Fathom (TransactionLogPage)
//
// CLICK-TO-FILTER:
// - Left-click filterable cell  → isolate (show only matching rows)
// - Right-click filterable cell → exclude (remove matching rows)
// - Chips in FilterBar let you re-include excluded values
// - Filter state lives in parent (useTransactionFilters at page level)
// =============================================================================

type SortKey = "date" | "cash" | "revenue" | "outcome" | "client" | "closer" | "setter" | "warmth";
type SortDir = "asc" | "desc";

// Warmth is free-text ordinal buckets; localeCompare would sort them alphabetically, not
// coldest→warmest. This map is pinned to the LIVE Rayston vocabulary (canonical + legacy/typo
// variants). Annotated Record<string, number> so a string index doesn't trip TS7053 under strict.
// "installment payment" is a payment marker, not a warmth bucket → parked at a high ordinal.
const WARMTH_ORDER: Record<string, number> = {
  "Less than a week": 0,
  "Less than 1 month": 1,
  "a couple weeks": 1,
  "a couple weeks ": 1,
  "1 to 2 weeks": 2,
  "3 weeks to 1 month": 3,
  "1 month": 3,
  "1 months": 3,
  "1 to 3 months": 4,
  "2 to 3 months": 4,
  "3 to 6 months": 5,
  "4 to 5 months": 5,
  "6 to 12 months": 6,
  "1+ years": 7,
  "1 to 3 years": 7,
  "installment payment": 900,
  "-": 999,
};

interface TransactionLogProps {
  transactions: SalesTransaction[];
  snippet?: boolean;
  timezone?: string;
  // Server-side pagination
  isLoading?: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  serverSidePagination?: boolean;
  // Snippet mode link
  viewAllLink?: string;
  viewAllCount?: number;
  // Tab toggle (lifted to page level so chart can respond)
  showLowTicket?: boolean;
  onToggleLowTicket?: (show: boolean) => void;
  // Date mode toggle (lifted to page level so the hook re-fetches): "attribution"
  // (Call Date) vs "event" (As Landed — event_date / Slack-feed arrival order).
  dateMode?: "attribution" | "event";
  onToggleDateMode?: (m: "attribution" | "event") => void;
  // Self-set only — show just closer-sourced calls (no setter attached → 12%).
  // Owned by the page so the chart + header count narrow with the table; the
  // local fallback keeps the component standalone-usable. See utils/selfSet.ts.
  selfSetOnly?: boolean;
  onToggleSelfSet?: (v: boolean) => void;
  /** How many self-set rows exist BEFORE the narrowing — shown on the pill. */
  selfSetCount?: number;
  // Click-to-filter (full mode only)
  filterChips?: FilterChip[];
  onApplyFilter?: (column: FilterableColumn, value: string, mode: FilterMode) => void;
  onRemoveFilter?: (column: FilterableColumn, value: string) => void;
  onClearColumnFilter?: (column: FilterableColumn) => void;
  onClearAllFilters?: () => void;
  // Country filter flyout (full mode, Sales Calls). Multi-select picker on the
  // header — replaces sort + click-to-filter on the Country column.
  countryOptions?: CountryOption[];
  selectedCountries?: Set<string> | null;
  onSetCountryFilter?: (values: string[]) => void;
}

// ---------------------------------------------------------------------------
// Outcome color mapping
// ---------------------------------------------------------------------------

const outcomeColor = (outcome: string) => BUCKETS[classifyOutcome(outcome)].badgeClass;

const isUnpaid = (outcome: string) => {
  const o = outcome.toLowerCase();
  return (
    o.includes("lost") ||
    o.includes("no show") ||
    o.includes("noshow") ||
    o.includes("no_show") ||
    o === "rescheduled" ||
    o.includes("cancel") ||
    o.includes("abandoned") ||
    o.includes("restored_from_abandon")
  );
};

// ---------------------------------------------------------------------------
// Filterable columns per tab
// ---------------------------------------------------------------------------

const SALES_CALL_FILTERABLE: Set<string> = new Set([
  "date",
  "closer",
  "setter",
  "warmth",
  "outcome",
  "program",
  "cash",
  "revenue",
]);

const LOW_TICKET_FILTERABLE: Set<string> = new Set([
  "date",
  "outcome",
  "program",
  "cash",
  "revenue",
]);

// ---------------------------------------------------------------------------
// SortHeader (static, outside render)
// ---------------------------------------------------------------------------

const SortHeader: React.FC<{
  label: string;
  field: SortKey;
  className?: string;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (field: SortKey) => void;
}> = ({ label, field, className = "", sortKey, sortDir, onSort }) => (
  <th
    className={`pr-3 token-11 cursor-pointer hover:text-glass-secondary select-none ${className}`}
    style={{
      paddingTop: 7,
      paddingBottom: 7,
      textTransform: "none",
      color: "var(--glass-text)",
      background: "transparent",
      fontWeight: 500,
      letterSpacing: 0,
    }}
    onClick={() => onSort(field)}
  >
    {label}
    {sortKey === field && <span className="ml-1">{sortDir === "asc" ? "\u25B2" : "\u25BC"}</span>}
  </th>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const TransactionLog: React.FC<TransactionLogProps> = ({
  transactions,
  snippet = false,
  timezone = "UTC",
  isLoading = false,
  isLoadingMore = false,
  hasMore = false,
  onLoadMore,
  serverSidePagination = false,
  viewAllLink: _viewAllLink,
  viewAllCount: _viewAllCount,
  // Tab toggle (lifted to page level)
  showLowTicket: showLowTicketProp,
  onToggleLowTicket,
  // Date mode toggle (lifted to page level)
  dateMode: dateModeProp,
  onToggleDateMode,
  // Self-set narrowing (lifted to page level)
  selfSetOnly: selfSetOnlyProp,
  onToggleSelfSet,
  selfSetCount,
  // Filter props
  filterChips,
  countryOptions,
  selectedCountries = null,
  onSetCountryFilter,
  onApplyFilter,
  onRemoveFilter,
  onClearColumnFilter,
  onClearAllFilters,
}) => {
  const { ref: revealRef, revealed } = useScrollReveal<HTMLDivElement>();
  // Use lifted state if provided (full page), otherwise local state (snippet)
  const [localShowLowTicket, setLocalShowLowTicket] = useState(false);
  const showLowTicket = showLowTicketProp ?? localShowLowTicket;
  const setShowLowTicket = onToggleLowTicket ?? setLocalShowLowTicket;
  // Same lift-or-local pattern for date mode.
  const [localDateMode, setLocalDateMode] = useState<"attribution" | "event">("attribution");
  const dateMode = dateModeProp ?? localDateMode;
  const setDateMode = onToggleDateMode ?? setLocalDateMode;
  // Same lift-or-local pattern for the self-set narrowing.
  const [localSelfSetOnly, setLocalSelfSetOnly] = useState(false);
  const selfSetOnly = selfSetOnlyProp ?? localSelfSetOnly;
  const setSelfSetOnly = onToggleSelfSet ?? setLocalSelfSetOnly;
  const [hideUnpaid, setHideUnpaid] = useState(false);
  const [showEmails, setShowEmails] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [timelineEmail, setTimelineEmail] = useState<string | null>(null);
  // Warmth column mode: "warmth" shows the warmth bucket; "leadHistory" swaps the whole column
  // to first-touch date + interaction count (right-click on the header toggles). Persists across
  // parent re-fetch / org / date change (it's local state, not derived from props).
  const [warmthMode, setWarmthMode] = useState<"warmth" | "leadHistory">("warmth");

  // ── Split by event type ──
  const { salesCalls, lowTicket } = useMemo(() => {
    const sc: SalesTransaction[] = [];
    const lt: SalesTransaction[] = [];
    transactions.forEach((t) => {
      if (t.eventType === "low_ticket_purchase") lt.push(t);
      else sc.push(t);
    });
    return { salesCalls: sc, lowTicket: lt };
  }, [transactions]);

  // ── Sort handler ──
  // Both setState calls sit at the event-handler top level, NOT nested inside a setState updater.
  // React StrictMode double-invokes UPDATERS (to surface impurity); the old code called
  // setSortDir((d) => flip) INSIDE the setSortKey updater, so in dev/test it toggled twice = no-op
  // (it only worked in prod, where updaters run once). This is the pure, StrictMode-safe form.
  const handleSort = useCallback(
    (key: SortKey) => {
      if (key === sortKey) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir("desc");
      }
    },
    [sortKey]
  );

  // ── Which columns are filterable for current tab ──
  const filterableColumns = showLowTicket ? LOW_TICKET_FILTERABLE : SALES_CALL_FILTERABLE;

  // ── Click-to-filter handler (left-click = isolate) ──
  const handleCellClick = useCallback(
    (e: React.MouseEvent<HTMLTableSectionElement>) => {
      if (snippet || !onApplyFilter) return;
      const td = (e.target as HTMLElement).closest("td[data-filter-column]") as HTMLElement | null;
      if (!td) return;
      const column = td.dataset.filterColumn as FilterableColumn;
      const value = td.dataset.filterValue;
      if (!column || value === undefined || !filterableColumns.has(column)) return;
      onApplyFilter(column, value, "isolate");
    },
    [snippet, onApplyFilter, filterableColumns]
  );

  // ── Right-click handler (exclude) ──
  const handleCellContextMenu = useCallback(
    (e: React.MouseEvent<HTMLTableSectionElement>) => {
      if (snippet || !onApplyFilter) return;
      const td = (e.target as HTMLElement).closest("td[data-filter-column]") as HTMLElement | null;
      if (!td) return;
      const column = td.dataset.filterColumn as FilterableColumn;
      const value = td.dataset.filterValue;
      if (!column || value === undefined || !filterableColumns.has(column)) return;
      e.preventDefault(); // suppress context menu for filterable cells only
      onApplyFilter(column, value, "exclude");
    },
    [snippet, onApplyFilter, filterableColumns]
  );

  // ── Sort + hide unpaid ──
  const activeTransactions = useMemo(() => {
    let list = showLowTicket ? lowTicket : salesCalls;

    if (hideUnpaid && !snippet) {
      list = list.filter((t) => !isUnpaid(t.callOutcome));
    }

    const sorted = [...list].sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      switch (sortKey) {
        case "date":
          return dir * (new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        case "cash":
          return dir * (a.cashCollected - b.cashCollected);
        case "revenue":
          return dir * (a.revenueClosed - b.revenueClosed);
        case "outcome":
          return dir * a.callOutcome.localeCompare(b.callOutcome);
        case "client":
          return dir * (a.clientName || "").localeCompare(b.clientName || "");
        case "closer":
          return dir * (a.closerName || "").localeCompare(b.closerName || "");
        case "setter":
          return dir * (a.setterName || "").localeCompare(b.setterName || "");
        case "warmth":
          // In Lead-History mode, sort by first-touch chronology; else by warmth ordinal.
          // Nullable warmthLevel is coerced with `?? ""` (TS2538 on a nullable index otherwise).
          if (warmthMode === "leadHistory") {
            return (
              dir *
              (new Date(a.firstTouchAt || 0).getTime() - new Date(b.firstTouchAt || 0).getTime())
            );
          }
          return (
            dir *
            ((WARMTH_ORDER[a.warmthLevel ?? ""] ?? 999) -
              (WARMTH_ORDER[b.warmthLevel ?? ""] ?? 999))
          );
        default:
          return 0;
      }
    });

    return sorted;
  }, [salesCalls, lowTicket, showLowTicket, hideUnpaid, sortKey, sortDir, snippet, warmthMode]);

  // ── Display list ──
  const displayed = snippet
    ? activeTransactions.filter((t) => t.eventType === "sales_call_outcome").slice(0, 7)
    : serverSidePagination
      ? activeTransactions
      : activeTransactions;

  // ── Daily volume for sparkline (snippet mode only) ──
  const dailyVolume = useMemo(() => {
    if (!snippet || transactions.length === 0) return [];
    const counts = new Map<string, number>();
    for (const t of transactions) {
      const day = t.timestamp ? timestampToOrgDateKey(t.timestamp, timezone) : "";
      if (day) counts.set(day, (counts.get(day) || 0) + 1);
    }
    const sorted = [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    return sorted.map(([, count]) => count);
  }, [snippet, transactions, timezone]);

  // ── Helper: build filter data attributes for a cell ──
  const filterAttrs = useCallback(
    (column: FilterableColumn, t: SalesTransaction) => {
      if (snippet) return {};
      if (!filterableColumns.has(column)) return {};
      return {
        "data-filter-column": column,
        "data-filter-value": getColumnValue(t, column),
        style: { cursor: "pointer" } as React.CSSProperties,
      };
    },
    [snippet, filterableColumns]
  );

  // ── Loading state ──
  if (isLoading) {
    return (
      <div ref={revealRef} className="liquid-glass anim-lift rounded-[20px] p-6 overflow-hidden">
        <h3 className="token-18 text-glass mb-4">Transaction Log</h3>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black/30"></div>
          <span className="ml-3 text-glass-muted">Loading transactions...</span>
        </div>
      </div>
    );
  }

  // ── Empty state ──
  if (transactions.length === 0) {
    return (
      <div ref={revealRef} className="liquid-glass anim-lift rounded-[20px] p-6 overflow-hidden">
        <h3 className="token-18 text-glass mb-4">Transaction Log</h3>
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <svg
            className="w-10 h-10 text-glass-muted opacity-40"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z"
            />
          </svg>
          {/* The self-set branch is NOT cosmetic. This early return renders a tree with
              no header, so the Self-Set toggle is not in the DOM here — narrowing to a
              window with zero self-set rows would otherwise dead-end the user: the copy
              would claim "no transactions in this date range" (false — there may be
              hundreds, just none self-set) with no control left to switch the filter
              back off. Name the real cause and give them the way out. */}
          {selfSetOnly ? (
            <>
              <p className="text-sm text-glass-muted">No self-set calls in this date range</p>
              <p className="text-xs text-glass-inactive">
                Other transactions may exist — the Self-Set filter is hiding them.
              </p>
              <button
                type="button"
                data-testid="self-set-clear-empty"
                onClick={() => setSelfSetOnly(false)}
                className="mt-1 px-3 py-1.5 text-xs font-semibold rounded-md liquid-glass-light text-glass anim-tap"
              >
                Show all transactions
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-glass-muted">No transactions in this date range</p>
              <p className="text-xs text-glass-inactive">
                Try widening the date range or adjusting filters
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={revealRef}
      className={`${
        snippet
          ? "overflow-hidden"
          : "liquid-glass anim-lift glass-dense rounded-[20px] p-5 overflow-hidden"
      } ${snippet ? (revealed ? "reveal-active" : "reveal-pending") : ""}`}
    >
      {/* ── Header: title, tab toggle, hide unpaid, count ── */}
      {!snippet && (
        <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="token-18 text-glass">Transaction Log</h3>
            {!snippet && (
              <div className="flex liquid-glass-light p-0.5">
                <button
                  onClick={() => setShowLowTicket(false)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all anim-tap ${
                    !showLowTicket
                      ? "text-glass bg-black/[0.05]"
                      : "text-glass-inactive bg-transparent"
                  }`}
                >
                  Sales Calls
                </button>
                <button
                  onClick={() => setShowLowTicket(true)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all anim-tap ${
                    showLowTicket
                      ? "text-glass bg-black/[0.05]"
                      : "text-glass-inactive bg-transparent"
                  }`}
                >
                  Low Ticket
                </button>
              </div>
            )}
            {/* Date mode toggle — "Call Date" (attribution) vs "As Landed" (event_date /
                Slack-feed arrival order). Only on the Sales Calls tab; Low Ticket rows
                have no call-time concept. */}
            {!showLowTicket && (
              <div className="flex liquid-glass-light p-0.5">
                <button
                  onClick={() => setDateMode("attribution")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all anim-tap ${
                    dateMode === "attribution"
                      ? "text-glass bg-black/[0.05]"
                      : "text-glass-inactive bg-transparent"
                  }`}
                >
                  Call Date
                </button>
                <button
                  onClick={() => setDateMode("event")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all anim-tap ${
                    dateMode === "event"
                      ? "text-glass bg-black/[0.05]"
                      : "text-glass-inactive bg-transparent"
                  }`}
                >
                  As Landed
                </button>
              </div>
            )}
            {/* Self-set narrowing — closer-sourced calls (no setter attached → 12%).
                Sales Calls tab only: Low Ticket rows have no setter dimension, so the
                concept doesn't apply there (see utils/selfSet.ts). Single toggle, not a
                segmented pair — it's a filter, not a mode. The count is the pre-filter
                population so it still reads as useful info while the filter is off. */}
            {!showLowTicket && (
              <div className="flex liquid-glass-light p-0.5">
                <button
                  type="button"
                  data-testid="self-set-toggle"
                  aria-pressed={selfSetOnly}
                  onClick={() => setSelfSetOnly(!selfSetOnly)}
                  title="Show only calls the closer sourced themselves (no setter attached)"
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all anim-tap flex items-center gap-1.5 ${
                    selfSetOnly
                      ? "text-glass bg-black/[0.05]"
                      : "text-glass-inactive bg-transparent"
                  }`}
                >
                  Self-Set
                  {selfSetCount !== undefined && selfSetCount > 0 && (
                    <span
                      className={`font-mono tabular-nums ${
                        selfSetOnly ? "text-ds-purple" : "text-glass-muted"
                      }`}
                    >
                      {selfSetCount}
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Tab-specific totals */}
            {(() => {
              const tabCash = activeTransactions.reduce(
                (sum, t) => sum + (t.cashCollected || 0),
                0
              );
              const tabRevenue = activeTransactions.reduce(
                (sum, t) => sum + (t.revenueClosed || 0),
                0
              );
              return tabCash > 0 || tabRevenue > 0 ? (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-ds-green font-semibold font-mono tabular-nums">
                    {formatUSD(tabCash)}
                  </span>
                  <span className="text-black/10">|</span>
                  <span className="text-glass font-semibold font-mono tabular-nums">
                    {formatUSD(tabRevenue)}
                  </span>
                  <span className="text-glass-muted">rev</span>
                </div>
              ) : null;
            })()}
            {!snippet && (
              <label className="flex items-center gap-2 text-xs text-glass-secondary cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showEmails}
                  onChange={() => setShowEmails(!showEmails)}
                  className="rounded border-black/[0.12] bg-black/[0.05] text-ds-accent focus:ring-[var(--accent)]/50"
                />
                Show emails
              </label>
            )}
            {!snippet && (
              <label className="flex items-center gap-2 text-xs text-glass-secondary cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={hideUnpaid}
                  onChange={() => setHideUnpaid(!hideUnpaid)}
                  className="rounded border-black/[0.12] bg-black/[0.05] text-ds-accent focus:ring-[var(--accent)]/50"
                />
                Hide unpaid
              </label>
            )}
            <span className="text-sm text-glass-muted font-mono tabular-nums">
              {activeTransactions.length} total
            </span>
          </div>
        </div>
      )}

      {/* ── Filter instructions (full mode only) ── */}
      {!snippet && (
        <div className="flex items-center gap-4 mb-3 token-11 text-glass-muted">
          <span>
            <kbd className="px-1.5 py-0.5 rounded bg-black/[0.04] bg-black/[0.05] text-glass-secondary font-mono token-10">
              Click
            </kbd>{" "}
            a cell to isolate
          </span>
          <span>
            <kbd className="px-1.5 py-0.5 rounded bg-black/[0.04] bg-black/[0.05] text-glass-secondary font-mono token-10">
              Click again
            </kbd>{" "}
            to clear
          </span>
          <span>
            <kbd className="px-1.5 py-0.5 rounded bg-black/[0.04] bg-black/[0.05] text-glass-secondary font-mono token-10">
              Right-click
            </kbd>{" "}
            to exclude
          </span>
        </div>
      )}

      {/* ── Filter bar (full mode only, only when filters active) ── */}
      {!snippet && filterChips && onRemoveFilter && onClearColumnFilter && onClearAllFilters && (
        <div className="mb-4">
          <FilterBar
            chips={filterChips}
            onRemoveFilter={onRemoveFilter}
            onClearColumn={onClearColumnFilter}
            onClearAll={onClearAllFilters}
          />
        </div>
      )}

      {/* ── Outcome summary pills (snippet mode only) ── */}
      {snippet && <OutcomeSummaryStrip transactions={salesCalls} variant="compact" />}

      {/* ── Table ── */}
      <div
        className={snippet ? "overflow-x-auto" : "overflow-auto -mx-5"}
        style={snippet ? undefined : { maxHeight: "70vh", overscrollBehavior: "contain" }}
      >
        <table
          className={snippet ? "w-full token-13" : "w-full token-13 glass-table"}
          style={snippet ? undefined : { tableLayout: "auto" }}
        >
          {/* WHY: thead needs opaque bg so data rows don't bleed through when scrolling under the sticky header.
             glass-dense = ~88% white over --bg (#F8F9FB) → effective surface ≈ #FAFBFC. */}
          <thead className={snippet ? "" : "sticky top-0 z-10 bg-[#FAFBFC]"}>
            <tr
              className={
                snippet
                  ? "border-b border-black/[0.06] text-left"
                  : "text-left border-b border-black/[0.08]"
              }
            >
              {snippet ? (
                <>
                  <th className="token-11 px-2 py-1.5 text-left">Date</th>
                  <th className="token-11 px-2 py-1.5 text-left">Client</th>
                  <th className="token-11 px-2 py-1.5 text-left">Closer</th>
                  <th className="token-11 px-2 py-1.5 text-left">Setter</th>
                  <th className="token-11 px-2 py-1.5 text-left">Outcome</th>
                  <th className="token-11 px-2 py-1.5 text-right">Cash</th>
                  <th className="token-11 px-2 py-1.5 text-right">Revenue</th>
                </>
              ) : (
                <>
                  <SortHeader
                    label="Date"
                    field="date"
                    className="pl-6 w-[1%] whitespace-nowrap"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    label="Client"
                    field="client"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  {!showLowTicket && (
                    <SortHeader
                      label="Closer"
                      field="closer"
                      className="w-[1%] whitespace-nowrap"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                  )}
                  {!showLowTicket && (
                    <SortHeader
                      label="Setter"
                      field="setter"
                      className="w-[1%] whitespace-nowrap"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                  )}
                  {!showLowTicket && (
                    <th className="px-2 py-1.5 text-left align-middle w-[1%] whitespace-nowrap">
                      {onSetCountryFilter && countryOptions ? (
                        <CountryFilterFlyout
                          options={countryOptions}
                          selected={selectedCountries}
                          onChange={onSetCountryFilter}
                        />
                      ) : (
                        <span
                          className="token-11 text-glass-secondary"
                          style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}
                        >
                          Country
                        </span>
                      )}
                    </th>
                  )}
                  {!showLowTicket && (
                    <th
                      className="pr-3 token-11 cursor-pointer select-none"
                      data-testid="warmth-header"
                      title="Left-click: sort · Right-click: swap to first-touch + interactions"
                      style={{
                        paddingTop: 7,
                        paddingBottom: 7,
                        textTransform: "none",
                        color: "var(--glass-text)",
                        background: "transparent",
                        fontWeight: 500,
                        letterSpacing: 0,
                      }}
                      onClick={() => handleSort("warmth")}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setWarmthMode((m) => (m === "warmth" ? "leadHistory" : "warmth"));
                      }}
                    >
                      {warmthMode === "warmth" ? "Warmth" : "Lead History"}
                      {sortKey === "warmth" && (
                        <span className="ml-1">{sortDir === "asc" ? "▲" : "▼"}</span>
                      )}
                    </th>
                  )}
                  <SortHeader
                    label="Outcome"
                    field="outcome"
                    className="w-[1%] whitespace-nowrap"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <th
                    className="pr-3 token-11"
                    style={{
                      paddingTop: 7,
                      paddingBottom: 7,
                      textTransform: "none",
                      color: "var(--glass-text)",
                      background: "transparent",
                      fontWeight: 500,
                      letterSpacing: 0,
                    }}
                  >
                    Program
                  </th>
                  <SortHeader
                    label="Cash"
                    field="cash"
                    className="text-right w-[1%] whitespace-nowrap"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    label="Revenue"
                    field="revenue"
                    className="text-right pr-6 w-[1%] whitespace-nowrap"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                </>
              )}
            </tr>
          </thead>
          <tbody
            className="divide-y divide-black/[0.04] divide-black/[0.04]"
            onClick={handleCellClick}
            onContextMenu={handleCellContextMenu}
          >
            {displayed.map((t, rowIdx) => {
              const date = t.dateOfCall || t.timestamp;
              // Event ("As Landed") mode shows the time too — the whole point is the
              // intra-day arrival sequence. Use toLocaleString (date+time) for event
              // mode and toLocaleDateString (date-only) for attribution — each is the
              // method that unambiguously renders its shape across all engines.
              const displayDate = !date
                ? "—"
                : dateMode === "event"
                  ? new Date(date).toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                      timeZone: timezone,
                    })
                  : new Date(date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      timeZone: timezone,
                    });
              // Compact cells in snippet mode to match leaderboard aesthetic
              const cellPad = snippet ? "px-2 py-1.5" : "pr-3 py-1.5";
              // Snippet-only: stagger row reveal via scroll-gated reveal-row. Full mode
              // (TransactionLogPage, real scrollable/paginated list) gets the standard
              // anim-pop mount cascade instead — capped + slowed so a large paginated
              // page doesn't take 45s to finish animating in.
              const revealCls = snippet ? "reveal-row" : "anim-pop";
              const revealDelay = snippet
                ? { animationDelay: `${rowIdx * 40}ms` }
                : { animationDelay: `${Math.min(rowIdx, 15) * 100}ms`, animationDuration: "480ms" };
              return (
                <tr
                  key={t.id}
                  className={`${revealCls} hover:bg-black/[0.02] hover:bg-black/[0.04] transition-colors`}
                  style={revealDelay}
                >
                  {/* Date + Fathom play icon */}
                  <td
                    className={`${cellPad} pl-6 text-glass-secondary whitespace-nowrap token-calendar`}
                    {...filterAttrs("date", t)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {displayDate}
                      {!snippet && t.fathomLink && (
                        <a
                          href={t.fathomLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-glass-muted hover:text-ds-accent transition-colors"
                          title="Watch call recording"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </a>
                      )}
                    </span>
                  </td>

                  {/* Client */}
                  <td
                    className={`${cellPad} whitespace-nowrap token-calendar`}
                    data-testid="client-cell"
                  >
                    {t.leadEmail ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setTimelineEmail(t.leadEmail!);
                        }}
                        className="text-ds-accent hover:opacity-80 transition-colors text-left cursor-pointer"
                      >
                        {showEmails ? t.leadEmail : toTitleCase(t.clientName || "")}
                      </button>
                    ) : (
                      <span className="text-glass">
                        {showEmails ? t.leadEmail || "—" : toTitleCase(t.clientName || "")}
                      </span>
                    )}
                  </td>

                  {/* Closer */}
                  {snippet ? (
                    <td
                      className={`${cellPad} text-glass-secondary whitespace-nowrap token-calendar`}
                    >
                      {t.closerName || "—"}
                    </td>
                  ) : !showLowTicket ? (
                    <td
                      className={`${cellPad} text-glass-secondary whitespace-nowrap token-calendar`}
                      {...filterAttrs("closer", t)}
                    >
                      {t.closerName || "—"}
                    </td>
                  ) : null}

                  {/* Setter */}
                  {snippet ? (
                    <td
                      className={`${cellPad} text-glass-secondary whitespace-nowrap token-calendar`}
                    >
                      {t.setterName || "—"}
                    </td>
                  ) : !showLowTicket ? (
                    <td
                      className={`${cellPad} text-glass-secondary whitespace-nowrap token-calendar`}
                      {...filterAttrs("setter", t)}
                    >
                      {/* Self-set rows read inconsistently at the source — the form value
                          is "No Setter" on rows since 2026-05-16 but absent on older ones
                          (which then fall back to "Unassigned"). Same deal, two labels.
                          Render one honest label for both so self-set is identifiable at a
                          glance without switching the filter on. Display only —
                          filterAttrs still carries the raw setterName, so the setter
                          column's click-to-filter values are unchanged. */}
                      {isSelfSet(t) ? (
                        <span className="text-ds-purple">Self-Set</span>
                      ) : (
                        t.setterName || "—"
                      )}
                    </td>
                  ) : null}

                  {/* Country (sales calls only, not snippet). Derived from the lead's phone,
                      resolved async by enrichLeadHistoryBatch. Only a filter target once
                      resolved — a pre-enrichment click would isolate the transient "Unknown". */}
                  {!snippet && !showLowTicket && (
                    <td
                      className={`${cellPad} whitespace-nowrap token-calendar`}
                      data-testid="country-cell"
                    >
                      {t.country ? (
                        <span className="text-glass-secondary">{t.country}</span>
                      ) : (
                        <span className="text-glass-muted">—</span>
                      )}
                    </td>
                  )}

                  {/* Warmth (sales calls only, not snippet). Right-clicking the header swaps this
                      column to Lead History (first-touch date + interaction count). In that mode
                      the cell is NOT a filter target (a first-touch isolate makes no sense). */}
                  {!snippet && !showLowTicket && (
                    <td
                      className={`${cellPad} whitespace-nowrap`}
                      data-testid="warmth-cell"
                      {...(warmthMode === "warmth" ? filterAttrs("warmth", t) : {})}
                    >
                      {warmthMode === "warmth" ? (
                        t.warmthLevel ? (
                          <span className="text-xs text-glass-secondary">{t.warmthLevel}</span>
                        ) : (
                          <span className="text-glass-muted">—</span>
                        )
                      ) : t.firstTouchAt ? (
                        <span className="text-xs text-glass-secondary tabular-nums">
                          {formatDateShort(new Date(t.firstTouchAt), timezone)}
                          <span className="ml-1.5 token-10 text-glass-muted">{`${t.txnCount ?? 0}×`}</span>
                        </span>
                      ) : (
                        <span className="text-glass-muted">—</span>
                      )}
                    </td>
                  )}

                  {/* Outcome */}
                  <td className={`${cellPad} whitespace-nowrap`} {...filterAttrs("outcome", t)}>
                    <span
                      className={`px-2 py-0.5 rounded-full ${snippet ? "token-10" : "text-xs"} font-medium ${outcomeColor(t.callOutcome)}`}
                    >
                      {toTitleCase(t.callOutcome.replace(/_/g, " "))}
                    </span>
                  </td>

                  {/* Program (full mode only) */}
                  {!snippet && (
                    <td
                      className={`${cellPad} text-glass-secondary whitespace-nowrap token-calendar`}
                      {...filterAttrs("program", t)}
                    >
                      {t.programSold || "—"}
                    </td>
                  )}

                  {/* Cash */}
                  <td
                    className={`${cellPad} text-right font-medium token-13m text-glass whitespace-nowrap`}
                    {...filterAttrs("cash", t)}
                  >
                    {formatUSD(t.cashCollected)}
                  </td>

                  {/* Revenue */}
                  <td
                    className={`${cellPad} pr-6 text-right token-13m text-glass whitespace-nowrap`}
                    {...filterAttrs("revenue", t)}
                  >
                    {formatUSD(t.revenueClosed)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Load more (server-side pagination) ── */}
      {!snippet && serverSidePagination && hasMore && onLoadMore && (
        <div className="text-center pt-4">
          <button
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="text-sm text-ds-accent hover:opacity-80 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingMore ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--accent)]"></span>
                Loading all...
              </span>
            ) : (
              "Load all transactions"
            )}
          </button>
        </div>
      )}

      {/* ── Daily activity sparkline (snippet mode) ── */}
      {snippet && dailyVolume.length > 2 && (
        <div className="mt-2 flex items-center justify-between px-1">
          <span className="token-9 uppercase tracking-wide text-glass-muted">Daily activity</span>
          <MiniSparkline
            data={dailyVolume}
            width={100}
            height={22}
            color="rgba(16, 185, 129, 0.12)"
            strokeColor="rgba(16, 185, 129, 0.5)"
          />
        </div>
      )}

      {/* View all link moved to SectionHeader badge area in OperationsDashboard */}

      {/* ── Modals ── */}
      <LeadTimelineModal
        email={timelineEmail || ""}
        isOpen={!!timelineEmail}
        onClose={() => setTimelineEmail(null)}
      />
    </div>
  );
};

export default TransactionLog;
